<template>
  <div>
    <div
      v-if="favoritesIsLoading || isLoading"
      class="loading-container"
    >
      <div>
        <i class="fa fa-spinner fa-spin fa-2x fa-fw" />
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <!-- Loop through the lists of grouped invoices -->
    <div
      v-for="group in groupedFavorites"
      v-else
      :key="group.title"
    >
      <!-- The title for this group of invoices -->
      <div
        v-if="group.items.length > 0"
        class="minorTitle flexBoxSpreadColumn"
      >
        <div>{{ group.title }}</div>
        <div class="w-100 flexBox marginSmallBottom flexEnd noWrap">
          <button
            v-if="toDelete[group.title].length > 0"
            class="button_redesign button--small marginSmallRight marginSmallTop button--blue"
            @click="bulkSelect(group.title, [])"
          >
            Clear
          </button>
          <button
            v-else
            class="button_redesign button--small marginSmallRight button--blue marginSmallTop"
            @click="bulkSelect(group.title, group.items.map(item => item.inventory_id))"
          >
            Select All
          </button>
          <button
            class="button_redesign button--small button--blue marginSmallTop"
            :disabled="toDelete[group.title].length === 0"
            @click="deleteItems(group.title)"
          >
            Delete
          </button>
        </div>
      </div>
      <!-- The list of items -->
      <ul v-if="group.items.length > 0">
        <!-- Loop through the items -->
        <li
          v-for="item in group.items"
          :id="'item-card-' + item.inventory_id"
          :key="item.inventory_id"
          class="itemRow"
        >
          <div class="marginSmallRight">
            <label class="checkbox-label">
              <input
                type="checkbox"
                name="delete[]"
                :checked="toDelete[group.title].indexOf(item.inventory_id) >= 0"
                @change="toggleDelete(group.title, item.inventory_id)"
              >
            </label>
          </div>
          <ItemCard :item_id="item.inventory_id" />
        </li>
      </ul>
    </div>

    <div v-if="!favoritesIsLoading && totalFavorites === 0">
      <p class="textCenter marginLargeTop">
        No results
      </p>
    </div>
    <!-- The pagination controls -->
    <div
      v-show="!favoritesIsLoading"
      v-if="paginationStatus && paginationStatus.total_pages > 0"
      class="textCenter"
    >
      <PaginationButtons
        :total-pages="paginationStatus.total_pages"
        :pages="paginationStatus.pages"
        :current-page="paginationStatus.page"
        :is-loading="favoritesIsLoading"
        @changePage="changePage"
      />
    </div>
  </div>
</template>

<script>
import FavoriteHeart from '@/components/FavoriteHeart.vue'
import ItemCard from '@/components/ItemCard'
import PaginationButtons from '@/components/Shared/PaginationButtons'

/**
 * The list of favorite items for AccountCard
 *
 * @displayName Favorite Items Component
 */
export default {
	name: 'FavoriteItems',

	components: {
		FavoriteHeart,
		ItemCard,
		PaginationButtons
	},

	props: ['listId'],

	/**
	 * The component data object
	 */
	data () {
		return {
			isLoading: false,
			groupBy: 'has_sold',
			toDelete: {
				'Sold': [],
				'Selling Now': []
			}
		}
	},

	computed: {
		/**
		 * The customer
		 *
		 * @public
		 */
		customer: function () {
			return this.$store.state.customer
		},
		/**
		 * Items in the store
		 *
		 * @public
		 */
		items: function () {
			return this.$store.state.items
		},

		/**
		 * Bidding statuses in the store
		 *
		 * @public
		 */
		biddingStatuses: function () {
			return this.$store.state.biddingStatuses
		},

		/**
		 * Our lists in the store
		 *
		 * @public
		 */
		lists: function () {
			return this.$store.state.lists
		},

		/**
		 * Whether an api call to get our favorites is in progress
		 *
		 * @public
		 */
		favoritesIsLoading: function () {
			return this.$store.state.favoritesIsLoading
		},
		/**
		 * Returns the pagination status of the favorite items list
		 *
		 * @public
		 */
		paginationStatus: function () {
			let status = {}
			let list = this.lists['LISTID_' + this.listId]
			if (typeof list !== 'undefined') {
				status.page = list.page
				status.total = list.total
				status.total_pages = list.total_pages
				status.pages = Array.from({ length: list.total_pages }, (_, i) => i + 1)
			}
			return status
		},

		/**
		 * The current page of items
		 *
		 * @public
		 */
		page: function () {
			let tempItems = []
			let list = this.lists['LISTID_' + this.listId]
			// the list exists in the store
			if (typeof list !== 'undefined') {
				// loop through all of the inventory ids on their bid list
				list.inventory_ids.forEach(value => {
					// the inventory id has a corresponding item and bidding_status in the store
					if (
						typeof this.items['ITEMID_' + value] !== 'undefined' &&
						typeof this.biddingStatuses['ITEMID_' + value] !== 'undefined'
					) {
						let tempItem = {
							inventory_id: value,
							target_url: this.items['ITEMID_' + value].card.target_url,
							image: this.items['ITEMID_' + value].card.image,
							title: this.items['ITEMID_' + value].card.title,
							has_sold: this.biddingStatuses['ITEMID_' + value].has_sold
						}
						tempItems.push(tempItem)
					}
				})
				return tempItems
			} else {
				return tempItems
			}
		},

		/**
		 * The invoices we are viewing right now
		 */
		groupedFavorites: function () {
			let temp = {}
			// we are grouping by selling and sold
			if (this.groupBy === 'has_sold') {
				temp.selling_now = {
					'title': 'Selling Now',
					'items': []
				}
				temp.sold = {
					'title': 'Sold',
					'items': []
				}
			}
			let list = this.lists['LISTID_' + this.listId]
			// the list exists in the store
			if (typeof list !== 'undefined') {
				// loop through all of the inventory ids on their bid list
				list.inventory_ids.forEach(value => {
					// the inventory id has a corresponding item and bidding_status in the store
					if (
						typeof this.items['ITEMID_' + value] !== 'undefined' &&
						typeof this.biddingStatuses['ITEMID_' + value] !== 'undefined'
					) {
						let tempItem = {
							inventory_id: value,
							target_url: this.items['ITEMID_' + value].card.target_url,
							image: this.items['ITEMID_' + value].card.image,
							title: this.items['ITEMID_' + value].card.title,
							has_sold: this.biddingStatuses['ITEMID_' + value].has_sold
						}
						if (tempItem.has_sold === true) {
							temp.sold.items.push(tempItem)
						} else {
							temp.selling_now.items.push(tempItem)
						}
					}
				})
				return temp
			}
			return temp
		},
		/**
		 * The total number of favorite items
		 *
		 * @public
		 */
		totalFavorites: function () {
			// Count all items in groupedFavorites
			let total = 0
			Object.keys(this.groupedFavorites).forEach((key) => {
				total += this.groupedFavorites[key].items.length
			})
			return total
		}
	},

	/**
	 * Gets called when component mounts
	 *
	 * @public
	 */
	mounted () {
		if (this.customer && this.customer.favorite_list_id) {
			// Fetch our first page of favorites
			this.$store.dispatch('getFavorites', {
				page: 1
			})
		}
	},

	methods: {
		/**
		 * Fetch paginated results from api
		 *
		 * @public
		 */
		fetchPage: function (page) {
			this.$store.dispatch('getFavorites', {
				page: page,
				callback: () => {
					const modal = document.getElementById('modalContainer')
					if (modal) {
						modal.scroll({
							top: 0,
							left: 0,
							behavior: 'smooth'
						})
					}
				}
			})
		},
		/**
		 * Select all items in a group
		 */
		bulkSelect: function (group, ids) {
			this.toDelete[group] = ids
		},
		/**
		 * Gets called when user paginates
		 *
		 * @public
		 */
		changePage: function (page) {
			this.fetchPage(page)
		},
		/**
		 * Delete the selected items
		 */
		deleteItems: async function (group) {
			this.isLoading = true
			await this.$store.dispatch('removeFavorites', {
				inventory_ids: this.toDelete[group],
				list_id: this.listId
			})
			this.toDelete[group] = []
			this.isLoading = false
			this.fetchPage(1)
		},
		/**
		 * Toggle the delete status of an item
		 * @param {*} inventoryId
		 */
		toggleDelete: function (group, inventoryId) {
			// If the item is already in the list, remove it
			if (this.toDelete[group].indexOf(inventoryId) >= 0) {
				this.toDelete[group] = this.toDelete[group].filter(item => item !== inventoryId)
			} else {
				this.toDelete[group].push(inventoryId)
			}
		}
	}
}
</script>

<style scoped>
.checkbox-label {
    position: relative;
    display: block;
    line-height: 20px;
}
.checkbox-label input {
    height: 16px;
    width: 16px;
}
</style>
